import { useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, Check, Upload } from "lucide-react";
import { useLang } from "../lib/i18n";

// Live camera capture with preview/retake; file upload is flagged as "upload".
export const CameraCapture = ({ onConfirm, busy }) => {
  const { t } = useLang();
  const video = useRef(null);
  const [on, setOn] = useState(false);
  const [shot, setShot] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!on) return;
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1600 } } })
      .then((s) => { stream = s; video.current.srcObject = s; video.current.play(); })
      .catch(() => { setErr(true); setOn(false); });
    return () => stream?.getTracks().forEach((x) => x.stop());
  }, [on]);

  const capture = () => {
    const v = video.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => { setShot({ blob: b, url: URL.createObjectURL(b), source: "live" }); setOn(false); }, "image/jpeg", 0.85);
  };
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setShot({ blob: f, url: URL.createObjectURL(f), source: "upload" });
  };

  if (shot) return (
    <div className="space-y-3">
      <img src={shot.url} alt="preview" className="w-full max-h-80 object-contain rounded-xl bg-slate-100" data-testid="photo-preview" />
      {shot.source === "upload" && <p className="text-xs font-bold text-amber-800">{t("photo_uploaded_flag")}</p>}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn-secondary" data-testid="photo-retake-button" onClick={() => setShot(null)}><RotateCcw size={18} />{t("retake")}</button>
        <button type="button" className="btn-primary" disabled={busy} data-testid="photo-confirm-button" onClick={() => onConfirm(shot.blob, shot.source)}><Check size={18} />{t("use_photo")}</button>
      </div>
    </div>
  );
  if (on) return (
    <div className="space-y-3">
      <video ref={video} playsInline muted className="w-full rounded-xl bg-black max-h-80" data-testid="camera-video" />
      <button type="button" className="btn-primary w-full" data-testid="photo-capture-button" onClick={capture}><Camera size={20} />{t("take_photo")}</button>
    </div>
  );
  return (
    <div className="space-y-2">
      <button type="button" className="btn-primary w-full" data-testid="photo-start-camera-button" onClick={() => { setErr(false); setOn(true); }}><Camera size={20} />{t("take_photo")}</button>
      {err && <p className="text-sm font-semibold text-red-700">{t("camera_unavailable")}</p>}
      <label className="btn-secondary w-full cursor-pointer text-sm" data-testid="photo-upload-label">
        <Upload size={16} /> {t("upload_file")}
        <input type="file" accept="image/*" className="hidden" data-testid="photo-file-input" onChange={pickFile} />
      </label>
    </div>
  );
};
